/**
 * CommentsSection — threaded discussion anchored to the CAP.
 *
 * Top-level comments render as cards; replies (parent_comment_id) render
 * indented underneath. Each top-level comment can be marked resolved/unresolved.
 * Replies inherit their parent's resolved state visually.
 *
 * Plain language: a "Discussion" section, not a "Threaded Comments Module."
 */

import React, { useState } from 'react';
import SectionCard from './SectionCard';
import Pill from './Pill';

const API = process.env.REACT_APP_API_URL;

function fmtWhen(ts) {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    return d.toLocaleString();
  } catch (_) {
    return String(ts);
  }
}

function CommentCard({ comment, capId, onRefresh, isReply, indent }) {
  const [showReply, setShowReply] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const resolved = !!comment.resolved_at;

  const postReply = async (e) => {
    e.preventDefault();
    if (!replyBody.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/pcap/caps/${capId}/comments`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          anchor_type: 'cap',
          anchor_id: capId,
          body: replyBody.trim(),
          parent_comment_id: comment.id,
        }),
      });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.message || 'Could not post reply');
      setReplyBody('');
      setShowReply(false);
      onRefresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleResolve = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/pcap/comments/${comment.id}/resolve`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ resolved: !resolved }),
      });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.message || 'Could not update');
      onRefresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const author =
    comment.author_external_name ||
    comment.author_name ||
    comment.author_external_email ||
    comment.author_email ||
    'Someone';
  const isExternal = !!comment.author_external_user_id;

  return (
    <div style={{ marginLeft: indent ? indent * 24 : 0 }}>
      <div
        className={`rounded-md ring-1 px-3 py-2 ${
          resolved ? 'bg-slate-50 ring-slate-100 opacity-75' : 'ring-slate-100'
        }`}
      >
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="text-[13px] font-medium text-slate-800">{author}</span>
          {isExternal && (
            <Pill tone="bg-orange-50 text-orange-800 ring-orange-200">FDR</Pill>
          )}
          <span className="text-[12px] text-slate-400">{fmtWhen(comment.created_at)}</span>
          {resolved && <Pill tone="bg-emerald-50 text-emerald-800 ring-emerald-200">Resolved</Pill>}
        </div>
        <p className="text-sm text-slate-700 whitespace-pre-wrap">{comment.body}</p>
        {!isReply && (
          <div className="mt-2 flex items-center gap-3 text-[12px]">
            <button
              type="button"
              onClick={() => setShowReply(v => !v)}
              className="text-slate-500 hover:text-slate-800 underline underline-offset-2"
            >
              {showReply ? 'Cancel reply' : 'Reply'}
            </button>
            <button
              type="button"
              onClick={toggleResolve}
              disabled={busy}
              className="text-slate-500 hover:text-slate-800 underline underline-offset-2 disabled:opacity-50"
            >
              {resolved ? 'Reopen' : 'Mark resolved'}
            </button>
          </div>
        )}
        {error && <p className="text-[12px] text-red-700 mt-1">{error}</p>}
      </div>

      {showReply && !isReply && (
        <form onSubmit={postReply} className="mt-2 ml-6 space-y-2">
          <textarea
            autoFocus
            value={replyBody}
            onChange={e => setReplyBody(e.target.value)}
            rows={2}
            placeholder="Reply…"
            className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
          />
          <div className="flex items-center gap-2">
            <button
              type="submit"
              disabled={busy || !replyBody.trim()}
              className="px-3 py-1.5 rounded-md bg-slate-900 text-white text-[12px] font-medium hover:bg-slate-800 disabled:opacity-50"
            >
              {busy ? 'Posting…' : 'Post reply'}
            </button>
            <button
              type="button"
              onClick={() => { setShowReply(false); setReplyBody(''); setError(null); }}
              className="px-3 py-1.5 rounded-md text-[12px] text-slate-600 hover:bg-slate-100"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default function CommentsSection({ data, onRefresh }) {
  const cap = data.cap;
  const comments = data.comments || [];

  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Build a parent->[children] map for one-level threading.
  const topLevel = comments.filter(c => !c.parent_comment_id);
  const repliesByParent = {};
  for (const c of comments) {
    if (c.parent_comment_id) {
      repliesByParent[c.parent_comment_id] = repliesByParent[c.parent_comment_id] || [];
      repliesByParent[c.parent_comment_id].push(c);
    }
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/pcap/caps/${cap.id}/comments`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          anchor_type: 'cap',
          anchor_id: cap.id,
          body: body.trim(),
        }),
      });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.message || 'Could not post');
      setBody('');
      onRefresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <SectionCard
      title="Discussion"
      hint="Talk through context, blockers, and decisions in plain words. Reply to a thread or mark it resolved when it's settled."
    >
      {topLevel.length === 0 ? (
        <p className="text-sm text-slate-400 italic">No comments yet.</p>
      ) : (
        <ul className="space-y-3">
          {topLevel.map(c => (
            <li key={c.id} className="space-y-2">
              <CommentCard comment={c} capId={cap.id} onRefresh={onRefresh} isReply={false} indent={0} />
              {(repliesByParent[c.id] || []).map(r => (
                <CommentCard key={r.id} comment={r} capId={cap.id} onRefresh={onRefresh} isReply={true} indent={1} />
              ))}
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={submit} className="mt-4 space-y-2">
        <textarea
          value={body}
          onChange={e => setBody(e.target.value)}
          rows={3}
          placeholder="Add to the discussion."
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
        />
        {error && <p className="text-[12px] text-red-700">{error}</p>}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={busy || !body.trim()}
            className="px-3 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-50"
          >
            {busy ? 'Posting…' : 'Post comment'}
          </button>
        </div>
      </form>
    </SectionCard>
  );
}
